import { memo } from 'react';

import useMagicPen from './useMagicPen';

import MagicPenTools from './MagicPenTools';
import FloatingButton from './FloatingButton';
import MagicPenCanvas from './MagicPenCanvas';
import SaveAndShareModal from './SaveAndShareModal';
import { IVideoDimensions } from '../VideoBox/videoBox.types';

import style from './magicPen.module.scss';

interface IMagicPenProps {
  isReadMode: boolean;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoDimensions: IVideoDimensions;
}

/**
 * MagicPen - Drawing canvas overlay for video annotations
 * Features: Pen tool for freehand drawing
 * Built with React Konva for high-performance canvas rendering
 */
const MagicPen = ({ isReadMode, videoRef, videoDimensions }: IMagicPenProps) => {
  const {
    // State
    isOpen,
    buttonPosition,
    floatingButtonPosition,
    shouldRender,
    canAnnotate,

    // Refs
    magicPenContainerRef,
    magicPenCanvasRef,

    // Handlers
    toggle,
    handleFloatingButtonPositionChange,
  } = useMagicPen({ videoRef, videoDimensions });

  return (
    <div className={style.magicPenContainer} ref={magicPenContainerRef}>
      {shouldRender && (
        <>
          {canAnnotate && !isReadMode && (
            <div className={style.magicPenDraggingArea}>
              {isOpen ? (
                <MagicPenTools onClose={toggle} buttonPosition={buttonPosition} />
              ) : (
                <FloatingButton
                  toggle={toggle}
                  position={floatingButtonPosition}
                  onPositionChange={handleFloatingButtonPositionChange}
                />
              )}
            </div>
          )}
          <SaveAndShareModal
            canvasRef={magicPenContainerRef}
            videoRef={videoRef}
            magicPenCanvasRef={magicPenCanvasRef}
          />
        </>
      )}

      <MagicPenCanvas ref={magicPenCanvasRef} canAnnotate={canAnnotate} />
    </div>
  );
};

export default memo(MagicPen);
